import { useState } from 'react';

import {
  ArrowRight,
  Building,
  CheckCircle,
  ChevronDown,
  Crosshair,
  Database,
  FolderOpen,
  Headphones,
  Shield,
  ShieldAlert,
  Star,
  Users,
  Zap,
} from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';

import { Button } from '@src/components/ui/button';
import { useNavigateToTop } from '@src/hooks/useNavigateToTop';

// ─── Data ────────────────────────────────────────────────────────────────────

const TIERS = [
  {
    id: 'starter',
    name: 'Starter',
    tagline: 'Core cybersecurity foundation',
    cta: 'Contact Sales',
    ctaVariant: 'outline',
    highlight: false,
    modules: ['Business', 'CMDB', 'Documents'],
    features: [
      'Business entity & process management',
      'Configuration management database',
      'AI-powered document management',
      'Up to 20 users',
      'Standard support (business hours)',
      '10 GB document storage',
      'Standard audit logging',
      'Email notifications',
      'BOSSS Role-based access control',
      '10-day backup retention',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    tagline: 'Everything you need to scale security',
    cta: 'Contact Sales',
    ctaVariant: 'default',
    highlight: true,
    badge: 'Most Popular',
    modules: ['GRC', 'IAM', 'Service Desk'],
    features: [
      'Everything in Starter',
      'AI powered Governance, Risk & Compliance (GRC)',
      'Identity & Access Management (IAM)',
      'IT Service Desk & ITSM',
      'Up to 150 users',
      'Priority support (24/5)',
      '100 GB document storage',
      '30-day backup retention',
      'Advanced analytics & reporting',
      'Advanced MITRE ATT&CK & OWASP mapping',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    tagline: 'Maximum security for complex organizations',
    cta: 'Contact Sales',
    ctaVariant: 'outline',
    highlight: false,
    modules: ['Marketplace Integrations', 'Blockchain', 'Pentest', 'XDR', 'SSO', 'AI Voice'],
    features: [
      'Everything in Pro',
      'Penetration testing & assessments',
      'Extended Detection & Response (XDR)',
      'Enterprise SSO (SAML / OIDC)',
      'AI voice security agents',
      'Custom blockchain ledger',
      'Dedicated success manager',
      '24/7 premium support + SLA',
      '2 TB document storage',
      '90+/custom day backup retention',
      'Custom compliance frameworks',
      'On-prem / private cloud options',
    ],
  },
];

const MODULES = [
  {
    id: 'business',
    name: 'Business',
    subtitle: 'Entity & Process Management',
    icon: Building,
    color: 'text-indigo-600',
    bg: 'bg-indigo-50',
    activeBg: 'bg-indigo-600',
    tier: 'starter',
    tierLabel: 'Starter',
    description:
      'A comprehensive operational backbone covering every dimension of your business — from entities, processes, and contracts to continuity planning, funding, and strategic objectives. Designed to give security and operations teams '
      + 'full organizational visibility.',
    highlights: [
      'Continuity & recovery planning (RTO/RPO)',
      'Legal contracts & vendor agreements',
      'Entities, subsidiaries & org hierarchy',
      'Expense tracking & budget management',
      'Business functions & process mapping',
      'Funding & spend tracking',
      'Locations — offices, data centers, geo-compliance',
      'Objectives & OKRs aligned to security goals',
      'Product & service catalog management',
      'Project portfolio & milestones tracking',
    ],
  },
  {
    id: 'cmdb',
    name: 'CMDB',
    subtitle: 'Configuration Management',
    icon: Database,
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    activeBg: 'bg-blue-600',
    tier: 'starter',
    tierLabel: 'Starter',
    description:
      'A complete configuration management database covering every asset class in your environment — from cloud infrastructure and applications to IoT devices, secrets, and social media accounts. Gives you full-stack visibility with '
      + 'security posture tracked across all CI types.',
    highlights: [
      'AI models, APIs & app registrations',
      'Applications & software inventory (EOL/CVE)',
      'Cloud resources across all providers',
      'Devices — servers, workstations, IoT & drones',
      'Databases, disks & storage inventory',
      'DNS, networks, VLANs & firewall mapping',
      'Secrets vault — keys, certs & credentials',
      'Source code repositories & branching policies',
      'Backups, logs & SIEM source inventory',
      'SharePoints, shares & distribution lists',
      'Web properties & social media accounts',
      'Product lifecycle tracking (EOL / upgrade planning)',
    ],
  },
  {
    id: 'documents',
    name: 'Documents',
    subtitle: 'AI Document Management',
    icon: FolderOpen,
    color: 'text-orange-600',
    bg: 'bg-orange-50',
    activeBg: 'bg-orange-600',
    tier: 'starter',
    tierLabel: 'Starter',
    description:
      'A centralized document management system for all organizational files, policies, and compliance artifacts. Features AI-powered content search, confidentiality classification, PII detection, access controls, and full activity '
      + 'audit trails.',
    highlights: [
      'Folder hierarchy & bulk upload',
      'Search by content, title, or filename',
      'Confidentiality classification (Public → Top Secret)',
      'PII detection & sensitive data flagging',
      'Owner & entity ownership tracking',
      'Access control per document',
      'Activity log & version history',
      'Preview, share, move & download',
    ],
  },
  {
    id: 'grc',
    name: 'GRC',
    subtitle: 'Governance, Risk & Compliance',
    icon: Shield,
    color: 'text-purple-600',
    bg: 'bg-purple-50',
    activeBg: 'bg-purple-600',
    tier: 'pro',
    tierLabel: 'Pro',
    description:
      'A full-spectrum GRC platform spanning three pillars — Governance (frameworks, controls, policies, RACI), Risk (register, scenarios, vulnerabilities, incidents), and Compliance (certifications, evidence, control testing, due '
      + 'diligence). Supports NIST CSF, ISO 27001, SOC 2, FAIR, and custom frameworks.',
    highlights: [
      'Cybersecurity frameworks — NIST CSF, ISO 27001, SOC 2',
      'Controls catalog with testing & effectiveness ratings',
      'Policies, procedures & attestation workflows',
      'CSF & FAIR maturity matrix assessments',
      'Risk register with treatment plans & residual tracking',
      'Risk scenarios, threat actors & threat matrix',
      'Vulnerability management & scan scheduling',
      'Incident tracking, investigation & lessons learned',
      'Certification projects with evidence collection',
      'Control testing, exceptions & due diligence packages',
      'RACI matrices & authority/regulatory mapping',
      'Compliance records, actions & key risk indicators',
    ],
  },
  {
    id: 'iam',
    name: 'IAM',
    subtitle: 'Identity & Access Management',
    icon: Users,
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    activeBg: 'bg-amber-600',
    tier: 'pro',
    tierLabel: 'Pro',
    description:
      'A complete identity governance platform covering the full identity lifecycle — from onboarding invitations and account provisioning to RBAC, entitlement reviews, access request workflows, and privileged account tracking. '
      + 'Purpose-built for security-first organizations.',
    highlights: [
      'Identity repository with lifecycle & account correlations',
      'RBAC — role hierarchies, permissions & constraints',
      'Entitlement catalog — permissions, roles & access rights',
      'Access request workflows with approvals & provisioning',
      'Certifications with auditor attestations & validity periods',
      'People directory with org attributes & access history',
      'System groups & permission inheritance',
      'System users & privileged account tracking',
      'User invitations with role assignments & expiration',
      'Teams — members, SLA policies & workload balancing',
      'Account type definitions (users, services, privileged)',
    ],
  },
  {
    id: 'service-desk',
    name: 'Service Desk',
    subtitle: 'IT Service Management',
    icon: Headphones,
    color: 'text-green-600',
    bg: 'bg-green-50',
    activeBg: 'bg-green-600',
    tier: 'pro',
    tierLabel: 'Pro',
    description:
      'A full ITSM platform with ticket lifecycle management, SLA tracking, and operational analytics. Supports Incidents, Service Requests, Problems, Change Requests, and Change Approval (CAB) workflows — with List and Kanban views, '
      + 'workload heatmaps, and internal collaboration tools.',
    highlights: [
      'Incidents, Service Requests, Problems & Change Requests',
      'Change Advisory Board (CAB) approval workflow',
      'Full ticket lifecycle — Draft → Open → Resolved → Closed',
      'SLA tracking & breached SLA alerting',
      'Assignments, teams & priority management',
      'List & Kanban board views with filters',
      'Live dashboard — open tickets, resolution time & volume trend',
      'Workload heatmap by day & time',
      'File attachments & internal notes',
      'Activity log & threaded comments per ticket',
    ],
  },
  {
    id: 'pentest',
    name: 'Pentest',
    subtitle: 'Security Assessments',
    icon: Crosshair,
    color: 'text-rose-600',
    bg: 'bg-rose-50',
    activeBg: 'bg-rose-600',
    tier: 'enterprise',
    tierLabel: 'Enterprise',
    description:
      'A comprehensive penetration testing platform with 127 integrated security tools across 76+ containers, multi-engagement management, and 42 pre-built scan types — from web app and network testing to AI/LLM, mobile '
      + '(iOS/Android), IoT, cloud CSPM, DevSecOps/IaC, social engineering, and post-exploitation. Full findings lifecycle with CVSS scoring, automated PoC validation, and remediation tracking.',
    highlights: [
      '127 tools across 76+ containers — nmap, ZAP, sqlmap, nuclei & more',
      'Web app, network, mobile (iOS/Android) & API testing',
      'AI / LLM, Cloud / CSPM & DevSecOps / IaC scans',
      'Phishing campaigns, pretext calling & physical security',
      'IoT / embedded firmware & hardware testing',
      'Post-exploitation & traffic analysis (PCAP upload)',
      'OWASP Top 10 Quick Scan (~20 min)',
      'Findings with CVSS scores, severity & remediation status',
      'Live dashboard — engagements, critical findings & CVSS avg',
      'Scope breakdown by target type & active campaigns',
    ],
  },
  {
    id: 'xdr',
    name: 'XDR',
    subtitle: 'Extended Detection & Response',
    icon: ShieldAlert,
    color: 'text-red-600',
    bg: 'bg-red-50',
    activeBg: 'bg-red-600',
    tier: 'enterprise',
    tierLabel: 'Enterprise',
    description:
      'A full-spectrum XDR platform delivering real-time threat detection, MITRE ATT&CK coverage heatmaps, cloud security posture (Azure/Wazuh), managed identity risk monitoring, and pre-built incident response playbooks with TLP '
      + 'classifications. Tracks MTTD, MTTR, SLA compliance, and analyst workload metrics.',
    highlights: [
      'Real-time alerts with MTTD & MTTR tracking',
      'SLA compliance monitoring (target > 95%)',
      'MITRE ATT&CK coverage & detection heatmap',
      'Alert volume trends by severity (24h / 30d)',
      'Top attack vector analysis',
      'Cloud resources — Azure sync, isolation & vulnerability tracking',
      'Managed identities — compromised, locked out & No-MFA risk',
      'Azure Defender findings integration',
      'Wazuh & multi-provider integration sync status',
      'IR playbook templates with TLP (Green / Amber / Strict)',
      'Analyst performance & workload metrics',
      'Insights & recommendations engine',
    ],
  },
];

const COMPARISON_ROWS = [
  { category: 'Core Platform', feature: 'Business Management', starter: true, pro: true, enterprise: true },
  { category: 'Core Platform', feature: 'CMDB & Asset Management', starter: true, pro: true, enterprise: true },
  { category: 'Core Platform', feature: 'AI Document Management', starter: true, pro: true, enterprise: true },
  { category: 'Core Platform', feature: 'Standard Audit Logging', starter: true, pro: true, enterprise: true },
  { category: 'Core Platform', feature: 'User Seats', starter: 'Up to 20', pro: 'Up to 150', enterprise: 'Unlimited' },
  { category: 'Core Platform', feature: 'Document Storage', starter: '10 GB', pro: '100 GB', enterprise: '2 TB' },
  { category: 'Core Platform', feature: 'Backup Retention', starter: '10 days', pro: '30 days', enterprise: '90+/Custom days' },
  { category: 'Security', feature: 'Governance, Risk & Compliance', starter: false, pro: true, enterprise: true },
  { category: 'Security', feature: 'Identity & Access Management', starter: false, pro: true, enterprise: true },
  { category: 'Security', feature: 'MITRE ATT&CK & OWASP Mapping', starter: false, pro: true, enterprise: true },
  { category: 'Security', feature: 'Penetration Testing', starter: false, pro: false, enterprise: true },
  { category: 'Security', feature: 'Extended Detection & Response', starter: false, pro: false, enterprise: true },
  { category: 'Security', feature: 'Enterprise SSO (SAML / OIDC)', starter: false, pro: false, enterprise: true },
  { category: 'Operations', feature: 'IT Service Desk', starter: false, pro: true, enterprise: true },
  { category: 'Operations', feature: 'Blockchain Audit Trail', starter: false, pro: false, enterprise: true },
  { category: 'AI & Automation', feature: 'AI Voice Security Agents', starter: false, pro: false, enterprise: true },
  { category: 'AI & Automation', feature: 'AI Document Analysis', starter: true, pro: true, enterprise: true },
  { category: 'AI & Automation', feature: 'Advanced Analytics', starter: false, pro: true, enterprise: true },
  { category: 'Support', feature: 'Standard Support', starter: true, pro: false, enterprise: false },
  { category: 'Support', feature: 'Priority Support (24/5)', starter: false, pro: true, enterprise: false },
  { category: 'Support', feature: '24/7 Premium Support + SLA', starter: false, pro: false, enterprise: true },
  { category: 'Support', feature: 'Dedicated Success Manager', starter: false, pro: false, enterprise: true },
];

export const FAQS = [
  {
    q: 'How is pricing calculated?',
    a: 'All plans are priced based on your organization\'s size, modules selected, and deployment requirements. Contact our sales team for a tailored quote.',
  },
  {
    q: 'Can I customize user seats, storage, or backup retention?',
    a: 'Yes — all plans support customization of user seats, document storage, and backup retention beyond the default limits. Whether you\'re on Starter, Pro, or Enterprise, contact our sales team to tailor these limits to your '
      + 'organization\'s needs.',
  },
  {
    q: 'Can I start with one module and add more later?',
    a: 'Yes. The platform is fully modular. Start with the Starter plan (Business, CMDB, Documents) and upgrade to Pro or Enterprise as your security program matures. Module additions are seamless with zero data migration needed.',
  },
  {
    q: 'Is there a free trial?',
    a: 'We offer a guided 14-day proof-of-concept for Starter, and a fully managed pilot engagement for Pro/Enterprise. Schedule a demo and our team will set you up with a pre-configured tenant.',
  },
  {
    q: 'How does multi-tenancy work?',
    a: 'Every customer gets a fully isolated schema-based tenant with no shared data. BOSSS is purpose-built for multi-tenant cybersecurity operations, meeting SOC 2, ISO 27001, and NIST standards out of the box.',
  },
  {
    q: 'Can BOSSS be deployed on-premises or in a private cloud?',
    a: 'Yes. Enterprise customers can deploy BOSSS in their own cloud environment (AWS, Azure, GCP) or on-premises. Our team provides full deployment playbooks and ongoing support.',
  },
];

const TIER_COLORS = {
  starter: { ring: 'ring-blue-200', badge: 'bg-blue-100 text-blue-700', btn: 'border-blue-600 text-blue-600 hover:bg-blue-50' },
  pro: { ring: 'ring-amber-300', badge: 'bg-amber-100 text-amber-700', btn: 'bg-blue-600 hover:bg-blue-700 text-white' },
  enterprise: { ring: 'ring-slate-300', badge: 'bg-slate-100 text-slate-700', btn: 'border-slate-600 text-slate-700 hover:bg-slate-50' },
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function TierCard({ tier, onCta }) {
  const c = TIER_COLORS[tier.id];

  return (
    <motion.div
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      className={`relative flex flex-col rounded-2xl border bg-white p-8 shadow-sm transition-shadow hover:shadow-lg ${
        tier.highlight ? `ring-2 ${c.ring} shadow-md` : 'border-gray-200'
      }`}
    >
      {tier.badge && (
        <div className={`absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-4 py-1 text-xs font-bold ${c.badge} shadow`}>
          {tier.badge}
        </div>
      )}

      <div className="mb-6">
        <h3 className="text-xl font-bold text-gray-900">{tier.name}</h3>
        <p className="mt-1 text-sm text-gray-500">{tier.tagline}</p>
      </div>

      <div className="mb-6">
        <div className="text-2xl font-bold text-gray-900">Custom Pricing</div>
        <p className="mt-1 text-sm text-gray-500">Tailored to your organization</p>
      </div>

      <Button
        id={`tier-cta-${tier.id}`}
        variant={tier.highlight ? 'default' : 'outline'}
        className={`w-full mb-8 py-3 font-semibold ${tier.highlight ? 'bg-blue-600 hover:bg-blue-700 text-white' : c.btn}`}
        onClick={() => onCta(tier.id)}
      >
        {tier.cta}
        <ArrowRight className="ml-2 h-4 w-4" />
      </Button>

      <div className="mb-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">
          Included Modules
        </p>

        <div className="flex flex-wrap gap-1.5">
          {tier.modules.map((m) => (
            <span key={m} className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${c.badge}`}>
              {m}
            </span>
          ))}
        </div>
      </div>

      <div className="border-t border-gray-100 pt-6 flex-grow">
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">Features</p>

        <ul className="space-y-2.5">
          {tier.features.map((f) => (
            <li key={f} className="flex items-start gap-2 text-sm text-gray-700">
              <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-500" />
              {f}
            </li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
}

function ModuleTab({ module, active, onClick }) {
  const Icon = module.icon;
  return (
    <button
      type="button"
      id={`module-tab-${module.id}`}
      className={`w-full flex items-center gap-3 rounded-xl px-4 py-3 text-left transition-all ${
        active
          ? `${module.activeBg} text-white shadow-md`
          : 'hover:bg-gray-100 text-gray-700'
      }`}
      onClick={() => onClick(module.id)}
    >
      <div
        className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${
          active ? 'bg-white/20' : module.bg
        }`}
      >
        <Icon className={`h-5 w-5 ${active ? 'text-white' : module.color}`} />
      </div>

      <div className="min-w-0">
        <div className={`font-semibold text-sm ${active ? 'text-white' : 'text-gray-900'}`}>{module.name}</div>
        <div className={`text-xs truncate ${active ? 'text-white/70' : 'text-gray-500'}`}>{module.subtitle}</div>
      </div>

      <span
        className={`ml-auto flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
          active
            ? 'bg-white/20 text-white'
            : module.tier === 'starter'
              ? 'bg-blue-100 text-blue-700'
              : module.tier === 'pro'
                ? 'bg-amber-100 text-amber-700'
                : 'bg-slate-100 text-slate-700'
        }`}
      >
        {module.tierLabel}
      </span>
    </button>
  );
}

function ComparisonCell({ value }) {
  if (value === true) {
    return <CheckCircle className="mx-auto h-5 w-5 text-emerald-500" />;
  }
  if (value === false) {
    return <span className="mx-auto block h-5 w-5 text-center text-gray-300 leading-5">–</span>;
  }
  return <span className="text-sm text-gray-700 font-medium">{value}</span>;
}

function ComparisonTable() {
  const categories = [...new Set(COMPARISON_ROWS.map((r) => r.category))];
  const [openCats, setOpenCats] = useState(new Set(categories));

  function toggleCat(cat) {
    setOpenCats((prev) => {
      const next = new Set(prev);
      next.has(cat) ? next.delete(cat) : next.add(cat);
      return next;
    });
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
      {/* Header */}
      <div className="grid grid-cols-4 border-b border-gray-100 bg-gray-50 px-6 py-4">
        <div className="text-sm font-semibold text-gray-500">Feature</div>

        {TIERS.map((t) => (
          <div key={t.id} className="text-center text-sm font-bold text-gray-900">{t.name}</div>
        ))}
      </div>

      {categories.map((cat) => {
        const rows = COMPARISON_ROWS.filter((r) => r.category === cat);
        const open = openCats.has(cat);
        return (
          <div key={cat}>
            <button
              type="button"
              id={`compare-cat-${cat.toLowerCase().replace(/\s/g, '-')}`}
              className="w-full flex items-center justify-between px-6 py-3 bg-gray-50/60 hover:bg-gray-100 transition-colors border-b border-gray-100"
              onClick={() => toggleCat(cat)}
            >
              <span className="text-sm font-bold text-gray-700 uppercase tracking-wider">{cat}</span>

              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
              />
            </button>

            <AnimatePresence initial={false}>
              {open && (
                <motion.div
                  key="rows"
                  className="overflow-hidden"
                  transition={{ duration: 0.22 }}
                  exit={{ height: 0, opacity: 0 }}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                >
                  {rows.map((row, idx) => (
                    <div
                      key={row.feature}
                      className={`grid grid-cols-4 items-center px-6 py-3.5 border-b border-gray-50 ${
                        idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                      }`}
                    >
                      <div className="text-sm text-gray-700">{row.feature}</div>
                      <div className="text-center"><ComparisonCell value={row.starter} /></div>
                      <div className="text-center"><ComparisonCell value={row.pro} /></div>
                      <div className="text-center"><ComparisonCell value={row.enterprise} /></div>
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}

function FaqItem({ item, open, onToggle }) {
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between px-6 py-4 text-left bg-white hover:bg-gray-50 transition-colors"
        onClick={onToggle}
      >
        <span className="font-semibold text-gray-900 text-sm pr-4">{item.q}</span>

        <ChevronDown
          className={`h-5 w-5 flex-shrink-0 text-blue-600 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            exit={{ height: 0 }}
            initial={{ height: 0 }}
            className="overflow-hidden"
            animate={{ height: 'auto' }}
            transition={{ duration: 0.22 }}
          >
            <div className="px-6 pb-5 text-sm text-gray-600 leading-relaxed border-t border-gray-100 pt-4">
              {item.a}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function Pricing() {
  const onNavigateToTop = useNavigateToTop();
  const [activeModule, setActiveModule] = useState('business');
  const [openFaq, setOpenFaq] = useState(null);

  const module = MODULES.find((m) => m.id === activeModule);
  const ModuleIcon = module.icon;

  function handleCtaClick() {
    onNavigateToTop('/contact');
  }

  return (
    <div className="min-h-screen bg-white">
      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 py-28">
        {/* Glowing orbs */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute top-0 left-1/4 h-96 w-96 rounded-full bg-blue-600 opacity-10 blur-3xl" />
          <div className="absolute bottom-0 right-1/4 h-96 w-96 rounded-full bg-indigo-500 opacity-10 blur-3xl" />
        </div>

        <div className="relative z-10 mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            initial={{ opacity: 0, y: 24 }}
          >
            <div className="mb-4 inline-flex items-center rounded-full border border-blue-500/30 bg-blue-500/10 px-4 py-1.5 text-sm font-medium text-blue-400">
              <Zap className="mr-2 h-4 w-4" />
              Modular Cybersecurity Platform
            </div>

            <h1 className="mt-4 text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
              Simple,
              {' '}
              <span className="text-blue-400">Modular</span>
              {' '}
              Pricing
            </h1>

            <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-400">
              Start with the core platform and expand with the security modules you need.
              One unified platform — only pay for what you use.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── Pricing Cards ─────────────────────────────────────────────────── */}
      <section id="pricing-tiers" className="py-20 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8 md:grid-cols-3">
            {TIERS.map((tier) => (
              <TierCard tier={tier} key={tier.id} onCta={handleCtaClick} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Module Explorer ───────────────────────────────────────────────── */}
      <section id="modules" className="py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <div className="mb-3 inline-flex items-center rounded-full bg-blue-100 px-4 py-1.5 text-sm font-medium text-blue-700">
              <Shield className="mr-2 h-4 w-4" />
              Available Modules
            </div>

            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">Explore Each Module</h2>

            <p className="mx-auto mt-4 max-w-2xl text-lg text-gray-500">
              Select a module below to see what&apos;s included and which plan unlocks it.
            </p>
          </motion.div>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar tabs */}
            <div className="w-full lg:w-72 flex-shrink-0">
              <div className="flex flex-col gap-1.5 lg:sticky lg:top-24">
                {MODULES.map((m) => (
                  <ModuleTab
                    key={m.id}
                    module={m}
                    active={activeModule === m.id}
                    onClick={setActiveModule}
                  />
                ))}
              </div>
            </div>

            {/* Module detail panel */}
            <div className="flex-grow">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeModule}
                  exit={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  initial={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.25 }}
                  className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm"
                >
                  <div className="flex items-start gap-5 mb-8">
                    <div className={`flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-xl ${module.bg}`}>
                      <ModuleIcon className={`h-8 w-8 ${module.color}`} />
                    </div>

                    <div>
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="text-2xl font-bold text-gray-900">{module.name}</h3>

                        <span
                          className={`rounded-full px-3 py-1 text-xs font-semibold ${
                            module.tier === 'starter'
                              ? 'bg-blue-100 text-blue-700'
                              : module.tier === 'pro'
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-slate-100 text-slate-700'
                          }`}
                        >
                          Included in
                          {' '}
                          {module.tierLabel}
                          +
                        </span>
                      </div>

                      <p className="mt-1 text-gray-500">{module.subtitle}</p>
                    </div>
                  </div>

                  <p className="text-gray-700 leading-relaxed mb-8">{module.description}</p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {module.highlights.map((h) => (
                      <div
                        key={h}
                        className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 px-4 py-3"
                      >
                        <CheckCircle className={`h-5 w-5 flex-shrink-0 ${module.color}`} />
                        <span className="text-sm font-medium text-gray-800">{h}</span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 flex flex-wrap gap-3">
                    <Button
                      variant="outline"
                      id={`module-learn-${module.id}`}
                      className="border-blue-600 text-blue-600 hover:bg-blue-50"
                      onClick={() => onNavigateToTop(`/platform/${module.id}`)}
                    >
                      Learn more about
                      {' '}
                      {module.name}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>

                    <Button
                      id={`module-demo-${module.id}`}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={() => onNavigateToTop('/demo')}
                    >
                      Schedule a Demo
                    </Button>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* ── Feature Comparison Table ──────────────────────────────────────── */}
      <section id="compare" className="py-24 bg-gray-50">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">Compare Plans</h2>

            <p className="mt-4 text-lg text-gray-500">
              A full breakdown of everything included in each tier.
            </p>
          </div>

          <ComparisonTable />
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Frequently Asked Questions</h2>
          </div>

          <div className="flex flex-col gap-3">
            {FAQS.map((item, i) => (
              <FaqItem
                key={i}
                item={item}
                open={openFaq === i}
                onToggle={() => setOpenFaq(openFaq === i ? null : i)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── Bottom CTA ────────────────────────────────────────────────────── */}
      <section className="py-24 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-700">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            viewport={{ once: true }}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <div className="mb-4 inline-flex items-center rounded-full bg-white/10 border border-white/20 px-4 py-1.5 text-sm font-medium text-blue-100">
              <Star className="mr-2 h-4 w-4" />
              Trusted by security-first organizations
            </div>

            <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
              Ready to Unify Your Security Stack?
            </h2>

            <p className="mx-auto mt-4 max-w-2xl text-lg text-blue-100">
              Start your 14-day pilot today or talk to our team about a custom Enterprise deployment.
              No lock-in — cancel anytime.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                id="cta-schedule-demo"
                className="px-8 py-4 bg-gradient-to-r from-amber-400 to-amber-500 text-gray-900 hover:from-amber-500 hover:to-amber-600 font-bold text-base shadow-lg"
                onClick={() => onNavigateToTop('/demo')}
              >
                Schedule a Demo
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>

              <Button
                variant="outline"
                id="cta-talk-sales"
                className="px-8 py-4 border-2 border-white/70 text-white bg-white/10 hover:bg-white/20 font-semibold text-base backdrop-blur-sm"
                onClick={() => onNavigateToTop('/contact')}
              >
                Talk to Sales
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default Pricing;
